import { Fragment, useCallback, useEffect, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';
import { InviteUserModal } from './InviteUserModal';
import { GrantEditor, type GrantDraft } from './GrantEditor';

interface UserGrant {
    repoId: string;
    scope: GrantDraft['scope'];
    grantedAt: string;
    revokedAt: string | null;
}

interface UserRow {
    id: string;
    email: string;
    displayName: string | null;
    globalRole: 'admin' | 'reviewer';
    status: 'invited' | 'active' | 'suspended';
    invitedBy: string;
    invitedAt: string;
    activatedAt: string | null;
    lastLogin: string | null;
    grants: UserGrant[];
    roleIds: string[];
}

interface RoleSummary {
    id: string;
    name: string;
    accessType: 'docs-only' | 'docs-and-code';
}

function scopeLabel(scope: UserGrant['scope']): string {
    if (scope.type === 'all_content') return 'all content';
    if (scope.type === 'md_only') return 'markdown only';
    return `folders: ${scope.folders.join(', ')}`;
}

export function AdminUsersPage() {
    const { tokenReady, userInfo } = useAuth();
    const [users, setUsers] = useState<UserRow[] | null>(null);
    const [roles, setRoles] = useState<RoleSummary[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [showInvite, setShowInvite] = useState(false);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const [u, r] = await Promise.all([
                apiCall<UserRow[]>('/manage/users'),
                apiCall<RoleSummary[]>('/manage/roles').catch(() => [] as RoleSummary[])
            ]);
            setUsers(u);
            setRoles(r);
            setError(null);
        } catch (err) {
            setUsers([]);
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }, []);

    useEffect(() => {
        if (tokenReady) void load();
    }, [tokenReady, load]);

    async function setUserRoles(u: UserRow, roleIds: string[]) {
        setBusy(u.id);
        try {
            await apiCall(`/manage/users/${encodeURIComponent(u.id)}`, { method: 'PATCH', body: { roleIds } });
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    function toggleRole(u: UserRow, roleId: string) {
        const has = u.roleIds.includes(roleId);
        const next = has ? u.roleIds.filter((r) => r !== roleId) : [...u.roleIds, roleId];
        void setUserRoles(u, next);
    }

    async function updateUser(u: UserRow, patch: Partial<Pick<UserRow, 'globalRole' | 'status' | 'displayName'>>) {
        setBusy(u.id);
        try {
            await apiCall(`/manage/users/${encodeURIComponent(u.id)}`, { method: 'PATCH', body: patch });
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    async function addGrant(u: UserRow, draft: GrantDraft) {
        setBusy(u.id);
        try {
            await apiCall(`/manage/users/${encodeURIComponent(u.id)}/grants`, {
                method: 'POST',
                body: { repoId: draft.repoId, scope: draft.scope }
            });
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    async function revokeGrant(u: UserRow, repoId: string) {
        if (!window.confirm(`Revoke ${u.email}'s grant on ${repoId}?`)) return;
        setBusy(u.id);
        try {
            await apiCall(`/manage/users/${encodeURIComponent(u.id)}/grants/${repoId}`, { method: 'DELETE' });
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    async function deleteUser(u: UserRow) {
        const msg =
            `Delete ${u.email}? This removes their access immediately and revokes ${u.grants.filter((g) => !g.revokedAt).length} grant(s).\n\n` +
            `Note: if auto-provisioning is on for this user's email domain, they can re-create their account by signing in again. ` +
            `To permanently block someone, set their status to "suspended" instead.`;
        if (!window.confirm(msg)) return;
        setBusy(u.id);
        try {
            await apiCall(`/manage/users/${encodeURIComponent(u.id)}`, { method: 'DELETE' });
            setExpanded((cur) => (cur === u.id ? null : cur));
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBusy(null);
        }
    }

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Users</h1>
                    <p className="admin-page__sub">People invited to this tenant. They activate on first sign-in.</p>
                </div>
                <button className="btn btn--primary" onClick={() => setShowInvite(true)}>+ Invite user</button>
            </header>

            {error && <div className="admin-page__error">{error}</div>}
            {users === null && !error && <div className="admin-page__loading">Loading…</div>}
            {users && users.length === 0 && !error && (
                <div className="admin-page__empty">No users yet.</div>
            )}
            {users && users.length > 0 && (
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Email</th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Roles assigned</th>
                            <th>Legacy grants</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => {
                            const isSelf = u.id === userInfo?.objectId;
                            const activeGrants = u.grants.filter((g) => !g.revokedAt);
                            const open = expanded === u.id;
                            return (
                                <Fragment key={u.id}>
                                    <tr className={open ? 'admin-row--expanded' : ''}>
                                        <td>
                                            <button
                                                className="btn btn--ghost btn--small"
                                                onClick={() => setExpanded(open ? null : u.id)}
                                                aria-label={open ? 'Collapse' : 'Expand'}
                                            >
                                                {open ? '▾' : '▸'}
                                            </button>
                                        </td>
                                        <td>{u.email}</td>
                                        <td>{u.displayName ?? '—'}</td>
                                        <td>
                                            <select
                                                value={u.globalRole}
                                                disabled={isSelf || busy === u.id}
                                                onChange={(e) =>
                                                    void updateUser(u, {
                                                        globalRole: e.target.value as UserRow['globalRole']
                                                    })
                                                }
                                            >
                                                <option value="reviewer">reviewer</option>
                                                <option value="admin">admin</option>
                                            </select>
                                        </td>
                                        <td>
                                            <select
                                                value={u.status}
                                                disabled={isSelf || busy === u.id}
                                                onChange={(e) =>
                                                    void updateUser(u, { status: e.target.value as UserRow['status'] })
                                                }
                                            >
                                                <option value="invited">invited</option>
                                                <option value="active">active</option>
                                                <option value="suspended">suspended</option>
                                            </select>
                                        </td>
                                        <td>
                                            {u.roleIds.length === 0
                                                ? <em>none</em>
                                                : u.roleIds.length === 1
                                                    ? roles.find((r) => r.id === u.roleIds[0])?.name ?? <em>missing</em>
                                                    : `${u.roleIds.length} roles`}
                                        </td>
                                        <td>
                                            {activeGrants.length === 0
                                                ? u.globalRole === 'admin'
                                                    ? <em>all (admin)</em>
                                                    : <em>none</em>
                                                : `${activeGrants.length} repo${activeGrants.length === 1 ? '' : 's'}`}
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr className="admin-row--detail">
                                            <td></td>
                                            <td colSpan={6}>
                                                <div className="user-detail">
                                                    {u.globalRole === 'admin' ? (
                                                        <p className="form-hint">
                                                            Admins have access to all repos automatically. Roles and
                                                            overrides below are kept for reference but not enforced
                                                            while the user is an admin.
                                                        </p>
                                                    ) : null}
                                                    <h4>Roles</h4>
                                                    {roles.length === 0 ? (
                                                        <p className="form-hint">
                                                            No roles defined yet. Create one in <strong>Admin → Roles</strong>.
                                                        </p>
                                                    ) : (
                                                        <div className="user-roles">
                                                            {roles.map((r) => (
                                                                <label key={r.id} className="user-role-pill">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={u.roleIds.includes(r.id)}
                                                                        disabled={busy === u.id}
                                                                        onChange={() => toggleRole(u, r.id)}
                                                                    />
                                                                    <span>
                                                                        {r.name}
                                                                        <span className="user-role-pill__type">
                                                                            {r.accessType === 'docs-only' ? 'docs' : 'docs+code'}
                                                                        </span>
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <h4>Legacy per-user grants</h4>
                                                    <p className="form-hint">
                                                        <strong>These no longer grant access.</strong> Reviewers must
                                                        get access through a role above. These records are kept for
                                                        audit history and admin tooling, and can be revoked here for
                                                        housekeeping.
                                                    </p>
                                                    {activeGrants.length === 0 ? (
                                                        <p className="admin-page__empty">No legacy grants on file.</p>
                                                    ) : (
                                                        <ul className="grant-list">
                                                            {activeGrants.map((g) => (
                                                                <li key={g.repoId}>
                                                                    <code>{g.repoId}</code> · {scopeLabel(g.scope)}
                                                                    <button
                                                                        className="btn btn--ghost btn--small"
                                                                        disabled={busy === u.id}
                                                                        onClick={() => void revokeGrant(u, g.repoId)}
                                                                    >
                                                                        Revoke
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    <GrantEditor
                                                        onSubmit={(draft) => void addGrant(u, draft)}
                                                        submitLabel="Add override"
                                                        disabled={busy === u.id}
                                                    />

                                                    <div className="user-detail__danger">
                                                        <h4>Danger zone</h4>
                                                        <p className="form-hint">
                                                            Removes the user record and all their grants from this
                                                            app. Does not touch the upstream Google / Microsoft
                                                            account.
                                                        </p>
                                                        <button
                                                            className="btn btn--danger"
                                                            disabled={isSelf || busy === u.id}
                                                            onClick={() => void deleteUser(u)}
                                                            title={isSelf ? 'You cannot delete your own account' : ''}
                                                        >
                                                            Delete user
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {showInvite && (
                <InviteUserModal
                    onClose={() => setShowInvite(false)}
                    onInvited={() => {
                        void load();
                    }}
                />
            )}
        </div>
    );
}
